import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { H2 } from '../forms';
import { Body2, Body4, Button, LinkButton } from '../../components/common';
import { useMessageGetter } from 'react-message-context';
import { InputField } from '../../components/common/forms';
import { ButtonContainer, Form, FormContainer } from '../layoutComponents';
import FormError from '../../components/common/forms/FormError';
import { SetCredentialsFormState, setCredentialsSchema } from '../auth/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components/macro';
import { LoginCredentials, submitRegistrationCredentials } from '../../apis_kenno';
import { PasswordIsInvalidError, UsernameIsInvalidError } from '../../types/exceptions';
import { YSaatioRoutes } from '../../constants/routes';

const PasswordInstructions = styled.div`
  margin: 0 20px 20px 20px;
`;

type Props = {
  oauthState: string;
  registerSessionId: string;
  afterSubmit: (data: LoginCredentials) => void;
};

const YSaatioSetCredentialsStep: React.FC<Props> = ({ oauthState, afterSubmit, registerSessionId }) => {
  const translationPrefix = 'signup';
  const t = useMessageGetter(translationPrefix);
  const tYSaatio = useMessageGetter('ysaatio.signup.credentials');
  const [stepFailed, setStepFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('credentialsError');
  const [isBusy, setIsBusy] = useState(false);
  const formMethods = useForm<SetCredentialsFormState>({ resolver: zodResolver(setCredentialsSchema) });

  const onSubmit: SubmitHandler<SetCredentialsFormState> = async (data) => {
    const { username, password } = data;
    try {
      setIsBusy(true);
      await submitRegistrationCredentials(username, password, oauthState, registerSessionId);
      setStepFailed(false);
      afterSubmit({ username, password });
    } catch (error: any) {
      switch (true) {
        case error instanceof UsernameIsInvalidError:
          setErrorMessage('usernameError');
          break;
        case error instanceof PasswordIsInvalidError:
          setErrorMessage('passwordError');
          break;
        default:
          setErrorMessage('credentialsError');
      }
      setStepFailed(true);
    }
    setIsBusy(false);
  };

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <H2>{tYSaatio('title')}</H2>
        <Body2>{tYSaatio('description')}</Body2>
        <Form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <InputField
            fieldPath="username"
            translationPrefix={translationPrefix}
            type="email"
            allowDefaultSubmit
            required
            dark
          />
          <InputField
            fieldPath="password"
            translationPrefix={translationPrefix}
            type="password"
            autoComplete="new-password"
            allowDefaultSubmit
            required
            dark
            togglePassword
          />
          <PasswordInstructions>
            <Body4>{t('passwordRequirements')}</Body4>
          </PasswordInstructions>
          <InputField
            fieldPath="confirmPassword"
            translationPrefix={translationPrefix}
            type="password"
            autoComplete="new-password"
            required
            allowDefaultSubmit
            dark
            togglePassword
          />
          <Body2>{tYSaatio('afterPasswordDescription')}</Body2>
          {stepFailed && <FormError message={t(errorMessage)} />}
          <ButtonContainer>
            <Button type="submit" id="submit" primary disabled={isBusy}>
              {t('saveAndContinue')}
            </Button>
            <LinkButton to={YSaatioRoutes.SIGN_IN}>{t('cancel')}</LinkButton>
          </ButtonContainer>
        </Form>
      </FormProvider>
    </FormContainer>
  );
};

export default YSaatioSetCredentialsStep;
